<template>
  <main class="row content__page" v-if="post">
    <article class="column large-full entry format-standard" style="text-align: left;">
      <h1 class="entry__title">{{ post.title }}
        <br/>
        <el-tag v-if="post.category" type="info" effect="dark" size="small">
          <b>{{ post.category.name }}</b>
        </el-tag>
      </h1>
      <p class="post_date">{{ timeAgo(post.created_at) }}</p>
      <br clear="all" />
      <div class="entry__content">
        <component 
          v-for="content in post.contents"
          :is="contentComponent(content)" 
          :key="content.id" 
          :content="content.data">
        </component>
        
        <div v-if="post.source_url" class="entry__source" style="margin-top: 2rem;">
          <h4>
            {{ $t('post.source') }}:
            <a :href="post.source_url" target="_blank" rel="noopener noreferrer">
              {{ $t(`post.source_type.${sourceType}`) }}
            </a>
          </h4>
        </div>

      </div>
      <!-- Add other post page components like entry__pagenav and entry__related here if needed -->
    </article>
  </main>
  <div v-else>
    <p>Loading...</p>
  </div>
  <FacebookCommentComponent :url="currentUrl" />
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue';
import { timeAgo } from '@/utils/date'
import { useRoute } from 'vue-router';
import apolloClient from '@/services/apollo-client';
import TextContent from '@/components/Post/TextContentComponent.vue';
import ImageContent from '@/components/Post/ImageContentComponent.vue';
import HeadingContent from '@/components/Post/HeadingContentComponent.vue';
import FacebookCommentComponent from '@/components/Post/FacebookCommentComponent.vue';
import { GET_POST, INCREMENT_POST_VIEW_COUNT } from '@/graphql/posts';
import { useI18n } from 'vue-i18n';


export default {
  name: 'PostPage',
  components: {
    TextContent,
    ImageContent,
    HeadingContent,
    FacebookCommentComponent,
  },
  setup() {
    const { locale } = useI18n();
    const route = useRoute();
    const post = ref(null);
    const loading = ref(false);
    const error = ref(null);
    const currentUrl = ref(window.location.href.replace("http://localhost:8080", "https://enov.blog"));

    const jsonLd = computed(() => {
      if (!post.value) return '';
      return {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": currentUrl.value,
        },
        "headline": post.value.title,
        "image": post.value.thumbnail_url,
        "datePublished": post.value.created_at,
        "dateModified": post.value.updated_at,
        "author": {
          "@type": "Person",
          "name": post.value?.author?.name || "Author Name", // Replace with actual author name
        },
        "publisher": {
          "@type": "Organization",
          "name": "ENOV News",
          "logo": {
            "@type": "ImageObject",
            "url": "https://enov.group/logo.png", // Replace with actual logo URL
          },
        },
        "description": post.value.excerpt,
      };
    });

    const insertJsonLd = () => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.text = JSON.stringify(jsonLd.value);
      document.head.appendChild(script);
    };

    const updatePageTitle = () => {
      if (post.value) {
        document.title = `${post.value.title} - ENOV Blog`;
      }
    };

    const insertMetaTags = () => {
      if (!post.value) return;

      const oldMetaTags = document.querySelectorAll('meta[property^="og:"], meta[name^="twitter:"]');
      oldMetaTags.forEach(tag => tag.parentNode.removeChild(tag));

      const ogTags = [
        { property: 'og:title', content: post.value.title },
        { property: 'og:description', content: post.value.excerpt },
        { property: 'og:type', content: 'article' },
        { property: 'og:url', content: currentUrl.value },
        { property: 'og:image', content: post.value.thumbnail_url },
      ];

      const twitterTags = [
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: post.value.title },
        { name: 'twitter:description', content: post.value.excerpt },
        { name: 'twitter:image', content: post.value.thumbnail_url },
        { name: 'twitter:url', content: currentUrl.value },
      ];

      ogTags.forEach(tag => {
        const metaTag = document.createElement('meta');
        metaTag.setAttribute('property', tag.property);
        metaTag.setAttribute('content', tag.content);
        document.head.appendChild(metaTag);
      });

      twitterTags.forEach(tag => {
        const metaTag = document.createElement('meta');
        metaTag.setAttribute('name', tag.name);
        metaTag.setAttribute('content', tag.content);
        document.head.appendChild(metaTag);
      });
    };

    const incrementPostViewCount = async (id) => {
      try {
        await apolloClient.mutate({
          mutation: INCREMENT_POST_VIEW_COUNT,
          variables: { id }
        });
      } catch (err) {
        console.error('Failed to increment post view count:', err);
      }
    };

    const fetchPost = async () => {
      loading.value = true;
      try {
        const { data } = await apolloClient.query({
          query: GET_POST,
          variables: {
            id: route.params.id,
            language: locale.value
          },
        });
        post.value = data.post;
        updatePageTitle();
        insertMetaTags();
        insertJsonLd();

        setTimeout(() => {
          incrementPostViewCount(data.post.id);
        }, 5000);

      } catch (err) {
        error.value = err;
        console.error('Error fetching post:', err);
      } finally {
        loading.value = false;
      }
    };

    const contentComponent = (content) => {
      switch (content.type) {
        case 'TEXT':
          return 'TextContent';
        case 'IMAGE':
          return 'ImageContent';
        case 'HEADING':
          return 'HeadingContent';
        default:
          return 'div';
      }
    };

    const sourceType = computed(() => {
      const url = post.value?.source_url || '';
      return url.includes('youtube.com') || url.includes('youtu.be') ? 'video' : 'article';
    });

    onMounted(() => {
      fetchPost();
    });

    watch(route, () => {
      currentUrl.value = window.location.href;
    });

    return {
      post,
      loading,
      error,
      currentUrl,
      contentComponent,
      sourceType,
      timeAgo,
    };
  },
};
</script>

<style scoped>
p {
  margin-top: 15px;
}
</style>
